import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { register } from "../../services/authService"; // Importamos la función register
import { alpha, useTheme } from "@mui/material/styles";

const toDataUrl = (svg) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

const Register = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    type: "Boss",
    name: "",
    lastName: "",
    hireDate: "", // YYYY-MM-DD, sólo para Driver
    revenuePercentage: 40,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const primary = theme.palette?.primary?.main || "#1b4db1";
  const onSurface = isLight ? alpha("#0b1a2d", 0.9) : alpha("#ffffff", 0.92);
  const onSurfaceSubtle = isLight ? alpha("#0b1a2d", 0.6) : alpha("#ffffff", 0.7);
  const logoSrc = `${process.env.PUBLIC_URL}/logotaxi.png`;
  const inputBorderColor = isLight ? alpha(primary, 0.85) : "#ffffff";
  const focusShadow = isLight ? alpha(primary, 0.25) : alpha("#ffffff", 0.18);
  const waveOverlay = useMemo(
    () =>
      toDataUrl(
        `<svg width="1440" height="420" viewBox="0 0 1440 420" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="%2352b4ff" stop-opacity="0.32"/><stop offset="100%" stop-color="%23a78bfa" stop-opacity="0.22"/></linearGradient></defs><path d="M0,180 C240,320 480,120 720,210 C960,300 1200,160 1440,240 L1440,420 L0,420 Z" fill="url(#grad)" /></svg>`
      ),
    []
  );

  const Logo = () => (
    <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
      <Box
        component="img"
        src={logoSrc}
        alt="TaxiBilling logo"
        sx={{
          width: 168,
          height: 168,
          objectFit: "contain",
          display: "block",
        }}
      />
      <Typography
        variant="h3"
        fontWeight={900}
        mt={0.5}
        color={onSurface}
        textAlign="center"
      >
        Taxi Billing
      </Typography>
    </Box>
  );

  const fieldStyles = {
    "& label": { color: onSurfaceSubtle },
    "& label.Mui-focused": { color: onSurface },
    "& .MuiOutlinedInput-input": {
      color: onSurface,
      padding: "15px 16px",
      fontSize: "1rem",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: alpha(primary, 0.08),
      boxShadow: `0 8px 24px ${alpha(primary, 0.18)}`,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: inputBorderColor,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: inputBorderColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: inputBorderColor,
        boxShadow: `0 0 0 3px ${focusShadow}`,
      },
    },
    "& .MuiInputBase-input::placeholder": { color: onSurfaceSubtle },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "revenuePercentage" ? Number(value) : value,
    }));
  };

  const handleRegister = async () => {
    setError("");
    setSuccess("");
    try {
      // Llamamos al service con el objeto completo
      await register({
        username: formData.username,
        password: formData.password,
        type: formData.type,
        name: formData.name,
        lastName: formData.lastName,
        hireDate: formData.type === "Driver" ? formData.hireDate : undefined,
        revenuePercentage:
          formData.type === "Driver" ? formData.revenuePercentage : undefined,
      });
      setSuccess(
        "Usuario registrado exitosamente. Ahora puedes iniciar sesión."
      );
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message || "Error al registrar usuario");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "flex-start", md: "center" },
        px: { xs: 2.5, sm: 3, md: 4 },
        pt: { xs: 6, md: 6 },
        pb: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(circle at 15% 20%, rgba(82, 180, 255, 0.35), transparent 32%), radial-gradient(circle at 85% 12%, rgba(79, 230, 201, 0.28), transparent 26%), linear-gradient(135deg, #0a1635 0%, #0c1f3f 45%, #071427 100%)",
        color: "#f6f8ff",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: waveOverlay,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          opacity: 0.45,
          filter: "blur(0px)",
        }}
      />
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 520,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          border: "none",
          backgroundColor: "transparent",
          boxShadow: "none",
          color: onSurface,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Logo />
        <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center">
          Crear cuenta
        </Typography>

        {error && (
          <Alert severity="error" variant="filled">
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" variant="filled">
            {success}
          </Alert>
        )}

        <TextField
          label="Usuario"
          name="username"
          value={formData.username}
          onChange={handleChange}
          fullWidth
          variant="outlined"
          sx={fieldStyles}
        />
        <TextField
          label="Contraseña"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          fullWidth
          variant="outlined"
          sx={fieldStyles}
        />

        <FormControl fullWidth variant="outlined" sx={fieldStyles}>
          <InputLabel>Tipo de Usuario</InputLabel>
          <Select
            name="type"
            value={formData.type}
            label="Tipo de Usuario"
            onChange={handleChange}
          >
            <MenuItem value="Boss">Jefe</MenuItem>
            <MenuItem value="Driver">Conductor</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Nombre"
          name="name"
          value={formData.name}
          onChange={handleChange}
          fullWidth
          variant="outlined"
          sx={fieldStyles}
        />
        <TextField
          label="Apellidos"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          fullWidth
          variant="outlined"
          sx={fieldStyles}
        />

        {formData.type === "Driver" && (
          <>
            <TextField
              label="Fecha de alta"
              type="date"
              name="hireDate"
              value={formData.hireDate}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={fieldStyles}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Porcentaje recaudación (%)"
              type="number"
              name="revenuePercentage"
              value={formData.revenuePercentage}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={fieldStyles}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleRegister}
          size="large"
          fullWidth
          sx={{
            py: 1.35,
            fontWeight: 700,
            borderRadius: 12,
            border: `1px solid ${inputBorderColor}`,
            boxShadow: `0 8px 24px ${alpha(primary, 0.25)}`,
          }}
        >
          Registrarse
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate("/login")}
          fullWidth
          sx={{ py: 1.2, fontWeight: 600, borderRadius: 12 }}
        >
          Volver a Login
        </Button>
      </Paper>
    </Box>
  );
};

export default Register;
